import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Phone, MessageCircle, Mail, Clock, Baby, CheckCircle2, Loader2, ArrowRight, RotateCcw, Repeat2, Car } from 'lucide-react';
import { BUSINESS } from '@/lib/constants';
import PageSEO from '@/components/PageSEO';
import { webPage, breadcrumb } from '@/lib/schemas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getNow() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const h = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  return { date: `${y}-${m}-${d}`, time: `${h}:${min}` };
}

const buildInitial = () => {
  const { date, time } = getNow();
  return {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    pickup: '',
    dropoff: '',
    date,
    time,
    passengerCount: '',
    luggageCount: '',
    childSeatsEnabled: false,
    rearFacing: '0',
    forwardFacing: '0',
    booster: '0',
    notes: '',
    website: '', // honeypot
  };
};

export default function RequestRide() {
  const [form, setForm] = useState(buildInitial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [roundTrip, setRoundTrip] = useState(false);
  const [returnPickup, setReturnPickup] = useState('');
  const [returnDropoff, setReturnDropoff] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [returnTime, setReturnTime] = useState('');
  const [returnPassengers, setReturnPassengers] = useState('');
  const [returnLuggage, setReturnLuggage] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleReset = () => {
    setForm(buildInitial());
    setErrors({});
    setRoundTrip(false);
    setReturnPickup('');
    setReturnDropoff('');
    setReturnDate('');
    setReturnTime('');
    setReturnPassengers('');
    setReturnLuggage('');
  };

  const handleRoundTrip = (enabled) => {
    setRoundTrip(enabled);
    if (enabled) {
      setReturnPickup(form.dropoff);
      setReturnDropoff(form.pickup);
      setReturnDate(form.date);
      setReturnTime(form.time);
      setReturnPassengers(form.passengerCount);
      setReturnLuggage(form.luggageCount);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.pickup.trim()) e.pickup = 'Pickup location is required.';
    if (!form.dropoff.trim()) e.dropoff = 'Drop-off location is required.';
    if (!form.email.trim()) e.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email.';
    if (!form.date) e.date = 'Date is required.';
    if (!form.time) e.time = 'Time is required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fix the highlighted fields.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        pickup: form.pickup.trim(),
        dropoff: form.dropoff.trim(),
        datetime: `${form.date}T${form.time}`,
        passengerCount: form.passengerCount ? parseInt(form.passengerCount, 10) : null,
        luggageCount: form.luggageCount ? parseInt(form.luggageCount, 10) : null,
        childSeats: form.childSeatsEnabled ? {
          rearFacing: parseInt(form.rearFacing, 10),
          forwardFacing: parseInt(form.forwardFacing, 10),
          booster: parseInt(form.booster, 10),
        } : null,
        roundTrip: roundTrip ? {
          pickup: returnPickup.trim(),
          dropoff: returnDropoff.trim(),
          datetime: returnDate && returnTime ? `${returnDate}T${returnTime}` : null,
          passengerCount: returnPassengers ? parseInt(returnPassengers, 10) : null,
          luggageCount: returnLuggage ? parseInt(returnLuggage, 10) : null,
        } : null,
        notes: form.notes.trim(),
        website: form.website,
      };
      await axios.post(`${API}/ride-requests`, payload);
      setSubmitted(true);
      setForm(buildInitial);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please call us instead.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const countOptions = ['0', '1', '2', '3'];

  const REQUEST_SCHEMAS = [
    webPage('Request a Ride', 'Submit a ride request with Island Ride for local Staten Island rides, airport trips, or out-of-town travel.', '/request-a-ride'),
    breadcrumb('Request a Ride', '/request-a-ride'),
  ];

  return (
    <>
      <PageSEO
        title="Request a Ride — Staten Island Taxi & Car Service | Island Ride"
        description="Book a ride with Island Ride — Staten Island's local cab service. Call, WhatsApp, or submit a request. Airport trips, local rides, pre-installed child seats available."
        path="/request-a-ride"
        schemas={REQUEST_SCHEMAS}
      />
      {/* Single merged section: title + form + sidebar */}
      <section className="section-pad bg-secondary">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          {/* Page title — outside the card, inside the section */}
          <div className="mb-8">
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary" data-testid="request-ride-h1">
              Request a Ride
            </h1>
            <p className="mt-3 text-muted-foreground text-base sm:text-lg max-w-2xl">
              Submit your request and we'll contact you to confirm timing, availability, and fare.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-6">

          {/* Form */}
          <Card className="lg:col-span-3 p-6 sm:p-8 shadow-[var(--shadow-card)]" style={{ background: 'linear-gradient(to top, rgba(16,97,95,0.06), rgba(16,97,95,0))' }}>
            {submitted ? (
              <div className="text-center py-10" data-testid="request-form-success">
                <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-accent text-primary">
                  <CheckCircle2 className="h-7 w-7" />
                </div>
                <h2 className="mt-4 font-display text-2xl font-semibold">Request received</h2>
                <p className="mt-3 text-muted-foreground max-w-md mx-auto leading-relaxed">
                  Thanks — your request was received. Island Ride will contact you to confirm timing, availability, and fare. For urgent rides, call or WhatsApp{' '}
                  <a href={BUSINESS.phoneTel} className="font-semibold text-primary hover:underline">{BUSINESS.phone}</a>.
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                  <Button asChild data-testid="success-call-button">
                    <a href={BUSINESS.phoneTel}><Phone className="h-4 w-4" /><span className="ml-1.5">Call dispatch</span></a>
                  </Button>
                  <Button variant="secondary" onClick={() => setSubmitted(false)} data-testid="success-another-button">
                    Send another request
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" noValidate data-testid="request-form">
                {/* Honeypot */}
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={(e) => set('website', e.target.value)}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />

                {/* First + Last Name */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First name</Label>
                    <Input id="firstName" data-testid="request-form-first-name" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} placeholder="First name" />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last name</Label>
                    <Input id="lastName" data-testid="request-form-last-name" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} placeholder="Last name" />
                  </div>
                </div>

                {/* Pickup + Dropoff */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pickup">Pickup location <span className="text-destructive">*</span></Label>
                    <Input id="pickup" data-testid="request-form-pickup" value={form.pickup} onChange={(e) => set('pickup', e.target.value)} placeholder="e.g. 123 Main St, New Dorp" />
                    {errors.pickup && <p className="mt-1 text-xs text-destructive">{errors.pickup}</p>}
                  </div>
                  <div>
                    <Label htmlFor="dropoff">Drop-off location <span className="text-destructive">*</span></Label>
                    <Input id="dropoff" data-testid="request-form-dropoff" value={form.dropoff} onChange={(e) => set('dropoff', e.target.value)} placeholder="e.g. Newark Airport (EWR)" />
                    {errors.dropoff && <p className="mt-1 text-xs text-destructive">{errors.dropoff}</p>}
                  </div>
                </div>

                {/* Email + Phone */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email <span className="text-destructive">*</span></Label>
                    <Input id="email" type="email" data-testid="request-form-email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@example.com" />
                    {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input id="phone" type="tel" data-testid="request-form-phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(718) 555-0123" />
                  </div>
                </div>

                {/* Date + Time */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date">Date <span className="text-destructive">*</span></Label>
                    <Input id="date" type="date" data-testid="request-form-date" value={form.date} onChange={(e) => set('date', e.target.value)} />
                    {errors.date && <p className="mt-1 text-xs text-destructive">{errors.date}</p>}
                  </div>
                  <div>
                    <Label htmlFor="time">Time <span className="text-destructive">*</span></Label>
                    <Input id="time" type="time" data-testid="request-form-time" value={form.time} onChange={(e) => set('time', e.target.value)} />
                    {errors.time && <p className="mt-1 text-xs text-destructive">{errors.time}</p>}
                  </div>
                </div>

                {/* Passengers + Luggage */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="passengerCount">Passengers</Label>
                    <Input id="passengerCount" type="number" min="1" max="10" data-testid="request-form-passengers" value={form.passengerCount} onChange={(e) => set('passengerCount', e.target.value)} placeholder="Number of passengers" />
                  </div>
                  <div>
                    <Label htmlFor="luggageCount">Luggage</Label>
                    <Input id="luggageCount" type="number" min="0" max="20" data-testid="request-form-luggage" value={form.luggageCount} onChange={(e) => set('luggageCount', e.target.value)} placeholder="Number of bags" />
                  </div>
                </div>

                {/* Child seats toggle + details */}
                <div className="sm:w-1/2">
                <div className="rounded-lg border bg-secondary/50 p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Baby className="h-4 w-4 text-primary" />
                      <Label htmlFor="childSeats" className="cursor-pointer font-medium">Child seats needed</Label>
                    </div>
                    <Switch
                      id="childSeats"
                      checked={form.childSeatsEnabled}
                      onCheckedChange={(v) => set('childSeatsEnabled', v)}
                      data-testid="request-form-child-seat-toggle"
                    />
                  </div>

                  {form.childSeatsEnabled && (
                    <div className="grid grid-cols-3 gap-2 pt-1" data-testid="child-seat-options">
                      <div>
                        <Label htmlFor="rearFacing" className="text-xs">Rear-facing</Label>
                        <Select value={form.rearFacing} onValueChange={(v) => set('rearFacing', v)}>
                          <SelectTrigger id="rearFacing" data-testid="child-seat-rear-facing">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {countOptions.map((n) => (
                              <SelectItem key={n} value={n}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="forwardFacing" className="text-xs">Forward-facing</Label>
                        <Select value={form.forwardFacing} onValueChange={(v) => set('forwardFacing', v)}>
                          <SelectTrigger id="forwardFacing" data-testid="child-seat-forward-facing">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {countOptions.map((n) => (
                              <SelectItem key={n} value={n}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="booster" className="text-xs">Booster</Label>
                        <Select value={form.booster} onValueChange={(v) => set('booster', v)}>
                          <SelectTrigger id="booster" data-testid="child-seat-booster">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {countOptions.map((n) => (
                              <SelectItem key={n} value={n}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
                </div>

                {/* Notes */}
                <div>
                  <Label htmlFor="notes">Notes / special requests</Label>
                  <Textarea id="notes" rows={3} data-testid="request-form-notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Anything else we should know?" />
                </div>

                {/* Round trip toggle */}
                <div className="sm:w-1/2">
                <div className="rounded-lg border bg-secondary/50 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Repeat2 className="h-4 w-4 text-primary" />
                      <Label htmlFor="roundTrip" className="cursor-pointer font-medium">Round trip</Label>
                    </div>
                    <Switch
                      id="roundTrip"
                      checked={roundTrip}
                      onCheckedChange={handleRoundTrip}
                      data-testid="request-form-round-trip-toggle"
                    />
                  </div>

                  {roundTrip && (
                    <div className="mt-4 space-y-3 pt-3 border-t" data-testid="round-trip-fields">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label htmlFor="returnPickup" className="text-xs">Return pickup</Label>
                          <Input id="returnPickup" className="text-sm" data-testid="round-trip-pickup" value={returnPickup} onChange={(e) => setReturnPickup(e.target.value)} placeholder="Pickup" />
                        </div>
                        <div>
                          <Label htmlFor="returnDropoff" className="text-xs">Return drop-off</Label>
                          <Input id="returnDropoff" className="text-sm" data-testid="round-trip-dropoff" value={returnDropoff} onChange={(e) => setReturnDropoff(e.target.value)} placeholder="Drop-off" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label htmlFor="returnDate" className="text-xs">Return date</Label>
                          <Input id="returnDate" type="date" className="text-sm" data-testid="round-trip-date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} />
                        </div>
                        <div>
                          <Label htmlFor="returnTime" className="text-xs">Return time</Label>
                          <Input id="returnTime" type="time" className="text-sm" data-testid="round-trip-time" value={returnTime} onChange={(e) => setReturnTime(e.target.value)} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label htmlFor="returnPassengers" className="text-xs">Passengers</Label>
                          <Input id="returnPassengers" type="number" min="1" max="10" className="text-sm" data-testid="round-trip-passengers" value={returnPassengers} onChange={(e) => setReturnPassengers(e.target.value)} placeholder="Same" />
                        </div>
                        <div>
                          <Label htmlFor="returnLuggage" className="text-xs">Luggage</Label>
                          <Input id="returnLuggage" type="number" min="0" max="20" className="text-sm" data-testid="round-trip-luggage" value={returnLuggage} onChange={(e) => setReturnLuggage(e.target.value)} placeholder="Same" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3">
                  <Button type="submit" disabled={submitting} className="min-w-[180px]" data-testid="request-form-submit">
                    {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /><span className="ml-1.5">Sending...</span></>) : 'Send request'}
                  </Button>
                  <Button type="button" variant="ghost" className="bg-accent text-primary hover:bg-accent/80" onClick={handleReset} data-testid="request-form-reset">
                    <RotateCcw className="h-4 w-4" /><span className="ml-1.5">Reset</span>
                  </Button>
                </div>
              </form>
            )}
          </Card>

          {/* Contact sidebar */}
          <aside className="lg:col-span-2" data-testid="request-sidebar-contact">
            <Card className="p-6 sticky top-20" style={{ background: 'linear-gradient(to top, rgba(16,97,95,0.06), rgba(16,97,95,0))' }}>
              <h3 className="font-display text-lg font-semibold text-primary">Contact us directly</h3>
              <p className="mt-1 text-sm text-muted-foreground">For urgent rides, calling or WhatsApp is fastest.</p>
              <div className="mt-5 space-y-3 w-1/2 min-w-[180px]">
                <a
                  href={BUSINESS.phoneTel}
                  className="flex items-center gap-3 rounded-lg border p-3 transition-colors duration-150 hover:bg-secondary"
                  data-testid="sidebar-call-link"
                >
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-primary shrink-0">
                    <Phone className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{BUSINESS.phone}</p>
                    <p className="text-xs text-muted-foreground">Call dispatch</p>
                  </div>
                </a>
                <a
                  href={BUSINESS.whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 rounded-lg border p-3 transition-colors duration-150 hover:bg-secondary"
                  data-testid="sidebar-whatsapp-link"
                >
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-primary shrink-0">
                    <MessageCircle className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">WhatsApp</p>
                    <p className="text-xs text-muted-foreground">Message us</p>
                  </div>
                </a>
                <a
                  href={BUSINESS.emailMailto}
                  className="flex items-center gap-3 rounded-lg border p-3 transition-colors duration-150 hover:bg-secondary"
                  data-testid="sidebar-email-link"
                >
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-primary shrink-0">
                    <Mail className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[11px] font-medium">{BUSINESS.email}</p>
                    <p className="text-xs text-muted-foreground">Email us</p>
                  </div>
                </a>
              </div>
              <div className="mt-5 pt-4 border-t">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Car className="h-4 w-4 text-primary" />
                  <span>Sedans · Minivans · SUVs</span>
                </div>
              </div>
            </Card>
          </aside>

        </div>
        </div>
      </section>

      {/* Internal Links */}
      <section
        className="section-pad"
        style={{ background: 'linear-gradient(to bottom, rgba(16,97,95,0.06), rgba(16,97,95,0))' }}
        data-testid="request-ride-links"
      >
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary">
            Looking for something specific?
          </h2>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Local Rides', to: '/local-rides-staten-island' },
              { label: 'Airport Rides', to: '/airport-rides-staten-island' },
              { label: 'Service Area', to: '/staten-island-service-area' },
              { label: 'Contact', to: '/contact' },
            ].map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="flex items-center gap-3 rounded-xl bg-background border p-4 transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-md"
                data-testid={`request-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <span className="font-display text-sm font-semibold">{link.label}</span>
                <ArrowRight className="h-3.5 w-3.5 text-primary ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
